/**
 * NameTag Component
 * Specialized tag for displaying names with optional actions
 */

import React from "react";
import {
  Tag as ChakraTag, TagLabel, TagCloseButton, HStack, IconButton,
} from "@chakra-ui/react";
import { useColorModeValue } from "design-system/hooks/useColorModeValue";
import CDS from "design-system";
import { TagVariantToken, TagSizeToken } from "./Tag";

export interface NameTagProps {
  title: string;
  variant?: TagVariantToken;
  size?: TagSizeToken;
  closable?: boolean;
  onClose?: () => void;
  onClick?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  disabled?: boolean;
}

function NameTagComponent({
  title,
  variant = CDS.Tag.variant.neutral,
  size = CDS.Tag.size.medium,
  closable = false,
  onClose,
  onClick,
  onEdit,
  onDelete,
  disabled = false,
  ...props
}: NameTagProps) {
  // Pre-compute all color values using Camino brand colors
  const neutralBg = useColorModeValue(CDS.Color.primitive.gray(100));
  const neutralColor = useColorModeValue(CDS.Color.primitive.gray(700));

  const primaryBg = useColorModeValue(CDS.Color.primitive.brand(100));
  const primaryColor = useColorModeValue(CDS.Color.primitive.brand(700));

  const successBg = useColorModeValue(CDS.Color.primitive.green(100));
  const successColor = useColorModeValue(CDS.Color.primitive.green(700));

  const warningBg = useColorModeValue(CDS.Color.primitive.orange(100));
  const warningColor = useColorModeValue(CDS.Color.primitive.orange(700));

  const errorBg = useColorModeValue(CDS.Color.primitive.red(100));
  const errorColor = useColorModeValue(CDS.Color.primitive.red(700));

  const getVariantStyles = (variant: TagVariantToken) => {
    switch (variant._value) {
      case "primary":
        return {
          bg: primaryBg,
          color: primaryColor,
        };
      case "success":
        return {
          bg: successBg,
          color: successColor,
        };
      case "warning":
        return {
          bg: warningBg,
          color: warningColor,
        };
      case "error":
        return {
          bg: errorBg,
          color: errorColor,
        };
      case "neutral":
      default:
        return {
          bg: neutralBg,
          color: neutralColor,
        };
    }
  };

  const getSizeProps = (size: TagSizeToken) => {
    switch (size._value) {
      case "small":
        return { size: "sm" as const };
      case "medium":
        return { size: "md" as const };
      case "large":
        return { size: "lg" as const };
      default:
        return { size: "md" as const };
    }
  };

  const variantStyles = getVariantStyles(variant);
  const sizeProps = getSizeProps(size);

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onEdit) {
      onEdit();
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onDelete) {
      onDelete();
    }
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled && onClose) {
      onClose();
    }
  };

  return (
    <ChakraTag
      variant="solid"
      cursor={onClick ? "pointer" : "default"}
      opacity={disabled ? 0.6 : 1}
      onClick={handleClick}
      {...sizeProps}
      {...variantStyles}
      {...props}
    >
      <HStack spacing={1}>
        <TagLabel fontWeight="medium">
          {title}
        </TagLabel>

        {onEdit && (
          <IconButton
            aria-label="Edit name"
            size="xs"
            variant="ghost"
            onClick={handleEdit}
            isDisabled={disabled}
            minW="auto"
            w="auto"
            h="auto"
            p={1}
          >
            ✏️
          </IconButton>
        )}

        {onDelete && (
          <IconButton
            aria-label="Delete name"
            size="xs"
            variant="ghost"
            onClick={handleDelete}
            isDisabled={disabled}
            minW="auto"
            w="auto"
            h="auto"
            p={1}
          >
            🗑️
          </IconButton>
        )}
      </HStack>

      {closable && (
        <TagCloseButton
          onClick={handleClose}
          isDisabled={disabled}
        />
      )}
    </ChakraTag>
  );
}

// Public API - just the component
export const NameTag = NameTagComponent;
